// Pure wording helpers, ported from the Worker page (src/app.js in ketosource-app-admin-panel) so both panels say
// the same thing for the same data. Strings only: React escapes every value it renders.
import type { Row } from "./api";

/** "16 Sep": newer ICU writes "Sept"; the design (and every other screen) uses three letters. */
export const day = (t: string | number | null | undefined) =>
  t ? new Date(t).toLocaleDateString("en-GB", { day: "numeric", month: "short" }).replace("Sept", "Sep") : "—";
export const when = (t: string | number | null | undefined) =>
  t
    ? new Date(t).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }).replace("Sept", "Sep")
    : "—";
/** "10:42": a time on today's list. */
export const clock = (t: string | number | null | undefined) =>
  t ? new Date(t).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "—";
export const ml = (v: unknown) => (v == null ? "—" : `${v} ml`);
/** A change in weight or waist (REQ-ADM-018): signed, one decimal; the API computes it. */
export const chg = (v: number | null | undefined, u: string) =>
  v == null ? "—" : `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(v).toFixed(1)} ${u}`;
export const yn = (v: unknown) => (v === true ? "Yes" : v === false ? "No" : "—");
export const score = (v: unknown) => (v == null || v === "" ? "—" : `${v}/10`);

/** Plain words for the engine's keys; an unknown key shows as itself. */
const LABELS: Record<string, string> = {
  rise: "Dose up", rung_4_stop: "Stopped: severe reaction", rung_4_stop_again: "Stopped again",
  progress_review: "Progress review", goal_change_request: "Goal change request", severe_reaction: "Severe reaction",
  stopped_again: "Stopped again", powder_recommended: "Powder recommended", took_less_hold: "Took less: hold",
  at_target: "At target", already_emulsified: "Already emulsified", day2_unchanged: "Day 2 unchanged", start: "Start",
  severely_upset: "Severely upset", slightly_upset: "Slightly upset", moderately_upset: "Moderately upset",
  great: "Great", not_a_test_user: "No ticket (not a test user)", staff_hold: "Held by staff (weekly increase 0)",
  analyst_referral: "Results below target", exactly: "As planned", different: "Different amount",
  didnt: "Didn't take it", forgot: "forgot", stomach_reason: "stomach", ran_out: "ran out", fine: "Fine",
  unsettled: "Unsettled", loose: "Loose", rough: "Rough",
};
export const L = (k: unknown) => (typeof k === "string" && LABELS[k]) || (k ? String(k) : "—");

/** 1141 (GATE-662 A): what the person answered in the v7.3 check-in; "—" on older check-ins. */
export function tookAns(c: Row): string {
  if (!c.took_choice) return "—";
  if (c.took_choice === "different" && c.amount_entered != null)
    return `${L("different")}: ${c.amount_entered} ${c.amount_unit || "ml"}`;
  if (c.took_choice === "didnt" && c.didnt_reason)
    return `${L("didnt")} (${L(c.didnt_reason === "stomach" ? "stomach_reason" : c.didnt_reason)})`;
  return L(c.took_choice);
}

/** A referral alert's numbers (admin_referral_numbers), v3.1 PA1 words: "Day 7 average results rating 4.2 (cut-off 5)." */
export const hasRef = (r: Row | null | undefined) => !!r && (r.average != null || r.cut_off != null);
export const refLine = (r: Row | null | undefined) =>
  r && hasRef(r)
    ? `Day ${r.referral_day ?? "?"} average results rating ${r.average ?? "?"} (cut-off ${r.cut_off ?? "?"}).`
    : "";

/** Stomach comfort: how she takes it (the database's rung, 0-3). Never called "step": the plan step is the dose ladder. */
export const COMFORT: [string, string][] = [
  ["0", "0 · standard"], ["1", "1 · with food"], ["2", "2 · emulsified (with acacia)"], ["3", "3 · with food + emulsified"],
];
/** Rung 4 is the engine's stop (the plan pauses), never a comfort option. */
export const comfort = (r: unknown) =>
  r == null || r === "" ? "—" : String(r) === "4" ? "stop (plan paused)" : (COMFORT.find(([k]) => k === String(r))?.[1] ?? String(r));
/** The short form the before-and-after rows use: "2 · emulsified". */
export const comfortShort = (r: unknown) => comfort(r).replace(/ \(.*\)$/, "");

/** Staff change names. A version's "change" wins over the stored kind (dosing 24). */
export const KIND: Record<string, string> = {
  set_dose: "Dose or step change", pause: "Paused", resume: "Resumed", program: "Programme change",
  goal: "Goal change", goal_change: "Goal change", release: "Protocol version change",
};
export function kindOf(x: Row): string {
  const k = x.change || x.kind;
  const changed: string[] = x.changed || [];
  if (k === "set_dose" && changed.some((f) => f !== "dose" && f !== "step" && f !== "target"))
    return changed.includes("goal") ? "Goal change" : "Programme change";
  return KIND[k] || k;
}

/** 759: the weekly rise in ml per fine week. 0 = hold. */
export const wk = (v: unknown) => (v == null ? "—" : Number(v) === 0 ? "0 (hold)" : `${v} ml/week`);
/** v3.1 tile and summary words: "+2.5 ml a week"; 0 holds the dose. */
export const weekA = (v: unknown) =>
  v == null ? "—" : Number(v) === 0 ? "0 ml a week (holds the dose)" : `+${v} ml a week`;
/** No value = the protocol default (dosing 27): only a staff-set rise is stored. */
export function weekly(a: unknown, b: unknown, staff: boolean | null | undefined): string {
  if (staff === false || (b == null && staff == null))
    return `weekly increase back to protocol default${b != null ? ` (${wk(b)})` : ""}`;
  return `weekly increase ${a == null ? "default" : wk(a)} → ${wk(b)}${a != null && Number(b) > Number(a) ? " (faster)" : ""}`;
}
/** No doses_per_day = no daily maximum: timing-only protocols such as Feel great (dosing 24). */
export const dpd = (v: unknown) => (v == null ? "No daily limit" : String(v));

/** v3.1 change words: "step 3 → 2 (10 → 7.5 ml)" when both plan steps are known, else "dose 10 ml → 7.5 ml". */
export function doseMove(bDose: unknown, aDose: unknown, bStep?: unknown, aStep?: unknown): string {
  // A dose between two steps floors to the same step on both sides: the step says nothing, so say the dose.
  if (bStep != null && aStep != null && Number(bStep) !== Number(aStep)) return `step ${bStep} → ${aStep} (${bDose ?? "—"} → ${aDose ?? "—"} ml)`;
  return `dose ${ml(bDose)} → ${ml(aDose)}`;
}
/** "stomach comfort 0 → 2": the rung, never called "step". */
const rungWord = (r: unknown) => (r == null ? "—" : String(r) === "4" ? "stop" : String(r));
export const comfortMove = (b: unknown, a: unknown) => `stomach comfort ${rungWord(b)} → ${rungWord(a)}`;

export type Names = { caseType: (k: string) => string; product: (k: string | null | undefined) => string };

/** Server versions (admin_program_versions), newest first: only the fields that changed (D11). */
export function svChanged(x: Row, b: Row | null, n: Names): string {
  if (!b) return `Start at ${ml(x.dose_ml)}${x.step != null ? ` (step ${x.step})` : ""} · stomach comfort ${x.rung ?? "—"}`;
  const out: string[] = [];
  for (const k of (x.changed || []) as string[]) {
    if (k === "goal") out.push(`goal ${b.goal_level_2 || "—"} → ${x.goal_level_2 || "—"}`);
    else if (k === "product") out.push(`product ${n.product(b.product_key)} → ${n.product(x.product_key)}`);
    else if (k === "dose") out.push(doseMove(b.dose_ml, x.dose_ml, b.step, x.step));
    else if (k === "step") out.push(comfortMove(b.rung, x.rung));
    else if (k === "paused") out.push(x.paused ? "paused" : "restarted");
    else if (k === "instructions") out.push(x.instructions ? "new instructions" : "instructions removed");
    else if (k === "target") out.push(`target ${ml(b.target_dose_ml)} → ${ml(x.target_dose_ml)}`);
    else if (k === "release") out.push(`protocol ${b.release || "—"} → ${x.release || "—"}`);
    else if (k === "weekly_step") out.push(weekly(b.weekly_step_ml, x.weekly_step_ml, x.weekly_step_set_by_staff));
    else if (k === "doses_per_day") out.push(`doses a day ${dpd(b.doses_per_day)} → ${dpd(x.doses_per_day)}`);
  }
  // A pin to the release already in force changes no value (dosing 24): say what was set.
  if (x.change === "release" && !(x.changed || []).includes("release"))
    out.push(
      b.pinned_release && !x.pinned_release
        ? `protocol back to default (${x.release || "—"})`
        : `protocol set to ${x.release || "—"}`,
    );
  return (x.kind === "start" ? "New start: " : "") +
    (out.length ? out.join(", ") : `no change (${ml(x.dose_ml)} · stomach comfort ${x.rung ?? "—"})`);
}

// The fallback history when the versions call fails: programme changes from the algorithm (a check-in whose dose
// or step differs from the one before) and staff changes together, oldest = v1 (PF 7 Oct, D11/D14).
export type LocalVersion = Row & { n: number; prev: LocalVersion | null };
export function versions(d: Row): LocalVersion[] {
  const checkins: Row[] = d.checkins || [];
  const alg = checkins
    .filter((c) => c.kind === "start" || c.previous_dose_ml == null || Number(c.dose_ml) !== Number(c.previous_dose_ml) || c.rung !== c.previous_rung)
    .map((c) => ({
      id: c.id, src: "alg", kind: c.kind, at: c.at, dose_ml: c.dose_ml, rung: c.rung, step: c.step, product_key: c.product_key,
      target_dose_ml: c.target_dose_ml, reason: c.reason, engine: c.engine, paused: /^rung_4_stop/.test(c.reason || ""),
    }));
  const st = ((d.overrides || []) as Row[]).map((o) => ({
    id: o.id, src: "staff", at: o.at, kind: o.kind,
    dose_ml: o.kind === "pause" ? o.before?.dose_ml : o.dose_ml,
    rung: o.kind === "pause" ? o.before?.rung : o.rung,
    step: o.kind === "pause" ? o.before_step : o.step,
    product_key: o.product_key || o.before?.product_key, paused: o.kind === "pause", reason: o.reason,
    staff: o.staff_email, case_id: o.case_id, case_type: o.case_type, team_alert_id: o.team_alert_id, before: o.before,
    weekly: o.weekly_step_ml != null || !!(o.before && "weekly_step_ml" in o.before),
    wfrom: o.before?.weekly_step_ml, wto: o.weekly_step_ml,
  }));
  const v = ([...alg, ...st] as Row[]).sort(
    (x, y) => new Date(x.at).getTime() - new Date(y.at).getTime() || Number(x.src === "staff") - Number(y.src === "staff"),
  ) as unknown as LocalVersion[];
  v.forEach((x, i) => {
    x.n = i + 1;
    x.prev = v[i - 1] || null;
  });
  return v;
}
/** What changed from the version before, in words: only the fields that moved. */
export function changed(x: LocalVersion): string {
  const b = x.prev;
  const out: string[] = [];
  if (!b || x.kind === "start") return `${b ? "New start" : "Start"} at ${ml(x.dose_ml)} · stomach comfort ${x.rung ?? "—"}`;
  if (x.paused && !b.paused) out.push("paused");
  if (!x.paused && b.paused) out.push("restarted");
  if (Number(x.dose_ml) !== Number(b.dose_ml)) out.push(doseMove(b.dose_ml, x.dose_ml, b.step, x.step));
  if (x.rung !== b.rung && x.rung != null) out.push(comfortMove(b.rung, x.rung));
  if (x.product_key && b.product_key && x.product_key !== b.product_key)
    out.push(`product ${b.product_key} → ${x.product_key}`);
  if (x.weekly && Number(x.wto) !== Number(x.wfrom) && (x.wto != null || x.wfrom != null))
    out.push(weekly(x.wfrom, x.wto, x.wto == null ? false : null));
  return out.length ? out.join(", ") : `no change (${ml(x.dose_ml)} · stomach comfort ${x.rung ?? "—"})`;
}

/**
 * One staff change in words (v3.1 PA5): "step 3 → 2 (10 → 7.5 ml), stomach comfort 0 → 2", "Paused",
 * "First dose 5 ml, +1.5 ml a week". Only what moved is named.
 */
export function staffChange(r: Row): string {
  const b: Row = r.before || {};
  if (r.kind === "pause") return "Paused";
  if (r.kind === "start" || r.first === true) {
    return `First dose ${ml(r.dose_ml)}${r.weekly_step_ml != null ? `, ${weekA(r.weekly_step_ml)}` : ""}`;
  }
  const out: string[] = [];
  if (r.kind === "resume") out.push("Resumed");
  if (r.dose_ml != null && Number(r.dose_ml) !== Number(b.dose_ml)) out.push(doseMove(b.dose_ml, r.dose_ml, r.before_step, r.step));
  if (r.rung != null && b.rung != null && Number(r.rung) !== Number(b.rung)) out.push(comfortMove(b.rung, r.rung));
  if (Number(r.weekly_step_ml) !== Number(b.weekly_step_ml) && (r.weekly_step_ml != null || b.weekly_step_ml != null))
    out.push(weekly(b.weekly_step_ml, r.weekly_step_ml, r.weekly_step_ml == null ? false : null));
  if (r.goal_level_2 && b.goal_level_2 && r.goal_level_2 !== b.goal_level_2) out.push(`goal ${b.goal_level_2} → ${r.goal_level_2}`);
  if (r.product_key && b.product_key && r.product_key !== b.product_key) out.push(`product ${b.product_key} → ${r.product_key}`);
  return out.length ? out.join(", ") : KIND[r.kind] || r.kind || "—";
}

/** The action a form shows and saves: the one picked while it is still offered, else the first offered. */
export const pickAct = (acts: [string, string][], picked: string) =>
  acts.some(([k]) => k === picked) ? picked : acts[0][0];

/**
 * The queue (v3.1 PA1): hand-offs and alerts as one list, sorted by time left to reply, smallest first.
 * The API sends `items` already in that order with each customer's one deadline (reply_due_at, the same value
 * the app promised them); the sort here only keeps that order if an older API sends the two lists apart.
 */
export type QueueKind = "Hand-off" | "Alert";
export function queueRows(d: Row | undefined): [QueueKind, Row][] {
  const all: [QueueKind, Row][] = Array.isArray(d?.items)
    ? (d!.items as Row[]).map((x): [QueueKind, Row] => [x.kind === "alert" ? "Alert" : "Hand-off", x])
    : [
        ...((d?.handoffs || []) as Row[]).map((x): [QueueKind, Row] => ["Hand-off", x]),
        ...((d?.alerts || []) as Row[]).map((x): [QueueKind, Row] => ["Alert", x]),
      ];
  const t = (v: unknown) => (v ? new Date(v as string).getTime() : NaN);
  const key = (x: Row) => {
    const due = t(x.reply_due_at);
    return Number.isFinite(due) ? due : Number.POSITIVE_INFINITY;
  };
  return all
    .map((r, i) => [r, i] as const)
    .sort(([a, i], [b, j]) => key(a[1]) - key(b[1]) || (t(a[1].opened_at ?? a[1].at) || 0) - (t(b[1].opened_at ?? b[1].at) || 0) || i - j)
    .map(([r]) => r);
}

/** What kind of wait a row is, in the queue's words (PA1 Kind column and the waiting chip). */
export function waitKind(kind: QueueKind | "handoff" | "alert" | null | undefined, x: Row | null | undefined): string {
  if (kind === "Hand-off" || kind === "handoff") return "Analyst hand-off";
  if (x && hasRef(x.referral)) return L("analyst_referral");
  return x?.type ? L(x.type) : "Alert";
}

const pad = (n: number) => String(n).padStart(2, "0");
/** A span as "d hh:mm:ss" ("2 d 19:20:11"), whole seconds, never negative. */
export function dhms(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 86400)} d ${pad(Math.floor((s % 86400) / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
}
/** "Wed 31 Oct, 23:59" in London time: the deadline the customer was given. */
export function londonWhen(t: number): string {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: "Europe/London" })
      .formatToParts(new Date(t)).map((x) => [x.type, x.value]),
  );
  return `${p.weekday} ${p.day} ${p.month}, ${p.hour}:${p.minute}`;
}
export type Due = { tone: "ok" | "soon" | "overdue"; text: string; by: string };
/**
 * The reply countdown (PA1): amber under a day, red once overdue ("Overdue d hh:mm:ss"), with the promise beneath.
 * `now` is the browser clock corrected by the server's (server_now at load), so every screen counts to one deadline.
 */
export function countdown(dueAt: string | null | undefined, now: number): Due | null {
  const due = dueAt ? new Date(dueAt).getTime() : NaN;
  if (!Number.isFinite(due)) return null;
  const left = due - now;
  return {
    tone: left < 0 ? "overdue" : left < 86_400_000 ? "soon" : "ok",
    text: left < 0 ? `Overdue ${dhms(-left)}` : dhms(left),
    by: `Reply by ${londonWhen(due)}`,
  };
}
/** The chip on a waiting customer's header and search row: "<Kind>: waiting on a reply · d hh:mm:ss left". */
export function waitingChip(w: Row | null | undefined, now: number): { text: string; tone: Due["tone"] } | null {
  const c = w ? countdown(w.reply_due_at, now) : null;
  if (!w || !c) return null;
  const kind = waitKind(w.kind, w);
  return { tone: c.tone, text: c.tone === "overdue" ? `${kind}: waiting on a reply · ${c.text}` : `${kind}: waiting on a reply · ${c.text} left` };
}

/** Day N of a date on the plan (Day 1 = started_on), as the API counts it; null when there is no start. */
export function dayOf(at: string | number | null | undefined, startedOn: string | null | undefined): number | null {
  if (!at || !startedOn) return null;
  const d = new Date(at), s = new Date(`${startedOn}T00:00:00Z`);
  const ymd = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  return Math.round((ymd - s.getTime()) / 86_400_000) + 1;
}

export const IC_APP = "it51xug4";
export const icUrl = (id: string) => `https://app.intercom.com/a/inbox/${IC_APP}/inbox/conversation/${encodeURIComponent(id)}`;

/**
 * The plan state a chip shows (Damien 31 Oct). The database calls a customer "on" once a protocol row exists, even
 * with no product, no dose and no check-in yet (liz on PROD): that is "No plan", and her page offers Create programme.
 */
export function planStatus(p: Row | null | undefined): "on" | "paused" | "none" {
  if (!p || !p.status || p.status === "none") return "none";
  if (p.status === "paused") return "paused";
  return p.dose_ml == null && !p.product_key && !p.product ? "none" : "on";
}

/** A staff test account (Damien 31 Oct): any @ketosource.co address, or one the database already flags. */
export const isTest = (x: Row | null | undefined) =>
  !!x && (x.is_test === true || /@ketosource\.co$/i.test(String(x.email || "").trim()));

/**
 * The case a change belongs to (Damien 31 Oct: never typed by hand). The open alert or hand-off it answers already
 * has a case when an earlier staff change answered it: that case ID is reused. Anything else gets a new ID from the
 * database on save (null here).
 */
export function caseFor(link: string, overrides: Row[] | null | undefined): string | null {
  if (!link) return null;
  const [k, id] = link.split(":");
  const field = k === "alert" ? "team_alert_id" : "handoff_id";
  const hit = (overrides || []).find((o) => o && o[field] === id && o.case_id);
  return hit ? String(hit.case_id) : null;
}
